import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { Member } from '../lib/contract';
import { apiFetch } from '../lib/api';
import { useMembers } from '../lib/members';
import { useSession } from '../lib/session';
import { Btn, Field, PageHead } from '../components/ui/primitives';
import { UserCombo } from '../components/ui/UserCombo';
import { CountrySelector } from '../components/ui/CountrySelector';

const CRA_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'default', label: 'Default' },
  { value: 'important_1', label: 'Important — Class I' },
  { value: 'important_2', label: 'Important — Class II' },
  { value: 'critical', label: 'Critical' },
];

export function NewProduct() {
  const { session } = useSession();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // R-PROD-01: the creator becomes the product's owner/manager until reassigned.
  const { members } = useMembers(session.organizationId);
  // R-PROD-01 — the creator is the default owner/manager until reassigned.
  const defaultOwner = members.find((m) => m.id === session.userRef)
    ?? { id: session.userRef, name: session.userName, role: session.role === 'manager' ? 'Manager' : 'User' };

  const [name, setName] = useState<string>(() => searchParams.get('name') ?? '');
  const [owner, setOwner] = useState<Member | null>(defaultOwner);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [description, setDescription] = useState<string>('');
  const [craClass, setCraClass] = useState<string>('default');
  const [markets, setMarkets] = useState<string[]>([]);

  const USE_FIXTURES = (import.meta.env.VITE_USE_FIXTURES as string | undefined) !== 'false';

  async function create() {
    if (!name.trim()) { setError('Product name is required.'); return; }
    if (USE_FIXTURES) {
      // Demo console: creation is stubbed — return to the portfolio.
      navigate('/app/products');
      return;
    }
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(session, '/api/portfolio', {
        method: 'POST',
        body: JSON.stringify({
          kind: 'product',
          name: name.trim(),
          description: description.trim() || undefined,
          craClass,
          ownerUserRef: owner?.id,
        }),
      });
      if (!res.ok) throw new Error(res.status === 403 ? 'Creating products requires the Manager role.' : `create failed (${res.status})`);
      const { id } = (await res.json()) as { id: string };
      // KB hand-off: a product created from an ephemeral scan gets its SBOM
      // attached now (v1.0 → upload → async analysis; incidents appear when S13 runs).
      if (searchParams.get('fromScan') === '1') {
        try {
          const raw = sessionStorage.getItem('bp.scan');
          if (raw) {
            const scan = JSON.parse(raw) as { filename: string; content: string };
            const vRes = await apiFetch(session, '/api/portfolio', {
              method: 'POST', body: JSON.stringify({ kind: 'version', productId: id, label: 'v1.0', placedOnMarket: true }),
            });
            if (vRes.ok) {
              const { versionId, swVersionId } = (await vRes.json()) as { versionId: string; swVersionId?: string };
              let swId = swVersionId;
              if (!swId) {
                const s = await apiFetch(session, '/api/portfolio', {
                  method: 'POST', body: JSON.stringify({ kind: 'version-sw-lookup', versionId }),
                });
                if (s.ok) swId = ((await s.json()) as { swVersionId: string }).swVersionId;
              }
              if (swId) {
                await apiFetch(session, '/api/uploads', {
                  method: 'POST', body: JSON.stringify({ swVersionId: swId, filename: scan.filename, content: scan.content }),
                });
              }
            }
            sessionStorage.removeItem('bp.scan');
          }
        } catch { /* product exists; the SBOM can be re-uploaded from its page */ }
      }
      // Markets aren't part of the create contract — persist them straight after.
      if (markets.length > 0) {
        await apiFetch(session, '/api/portfolio', { method: 'PATCH', body: JSON.stringify({ productId: id, patch: { markets } }) });
      }
      navigate(`/app/products/${id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <>
      <PageHead
        title="New product"
        crumbs={
          <>
            <Link to="/app/products">Products</Link> ／ New product
          </>
        }
      />

      <div className="card" style={{ padding: 24 }}>
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            void create();
          }}
        >
          <Field
            label="Product name"
            req
            hint="The only thing we need to get started. Everything else can wait."
          >
            <input required
              className="input"
              placeholder="e.g. Gateway GW-300"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>

          <div className="assurance" style={{ marginTop: -4 }}>
            ✓ <b>You ({session.userName}) become this product's owner &amp; manager</b> — until you
            assign it to someone else.
          </div>

          <details className="more">
            <summary>More details</summary>
            <div className="inner">
              <Field
                label="Owner / Product manager"
                hint="Defaults to you, the creator. Reassign any time."
              >
                <UserCombo members={members} value={owner} onPick={setOwner} />
              </Field>

              <Field label="Description">
                <textarea
                  placeholder="What the device does, where it runs…"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Field>

              <Field label="CRA product classification">
                <select
                  className="select"
                  value={craClass}
                  onChange={(e) => setCraClass(e.target.value)}
                >
                  {CRA_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field
                label="Countries where it's sold"
                hint="Pre-filled from your organisation's default."
              >
                <CountrySelector selected={markets} editable onChange={setMarkets} />
              </Field>

              <Field label="Datasheets & documents" hint="document storage is coming online — nothing dropped here is stored yet, so the area is disabled to be honest about it">
                <div className="dropzone" style={{ opacity: 0.55 }}>
                  <div className="big">Document storage — coming next</div>
                  PDF datasheets & declarations of conformity will attach here
                </div>
              </Field>
            </div>
          </details>

          <div className="form-actions">
            {error && <div className="hint" style={{ color: 'var(--danger, #c0392b)' }}>{error}</div>}
            <Btn variant="ghost" onClick={() => navigate('/app/products')}>
              Cancel
            </Btn>
            <Btn variant="primary" type="submit">
              Create product
            </Btn>
          </div>
        </form>
      </div>
    </>
  );
}
